import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import {
  normalizeMenu, currencySymbol, fontStack, IMG_RADIUS, BTN_RADIUS,
  LABELS, effectivePrice, priceNum, fmtMoney, tr,
} from "@/lib/menu";

function Badge({ id, lang, style, sym }) {
  const L = LABELS[id];
  if (!L) return null;
  const name = lang === "ar" ? L.ar : L.name;
  const base = "inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 leading-none";
  const st = style === "solid"
    ? { background: L.color, color: "#fff" }
    : style === "outline"
      ? { border: `1px solid ${L.color}`, color: L.color }
      : { background: `${L.color}22`, color: L.color };
  return <span className={base} style={st}><Icon name={L.icon} className="h-2.5 w-2.5" />{name}</span>;
}

export default function MenuView({ data, company = {}, publicId, live = false, table = "", features = {} }) {
  const cartOn = features.cart !== false;
  const tableLabel = features.tables !== false ? table : "";
  const d = useMemo(() => normalizeMenu(data || {}), [data]);
  const th = d.theme;
  const lang = d.language || "en";
  const rtl = !!d.rtl;
  const sym = currencySymbol(d.restaurant.currency);
  const cats = (d.categories || []).filter((c) => !c.hidden && (c.items || []).some((i) => i));
  const featured = useMemo(() => {
    const out = [];
    cats.forEach((c) => (c.items || []).forEach((it) => { if (it.featured || (it.labels || []).includes("special")) out.push(it); }));
    return out.slice(0, 8);
  }, [d]);

  const [cart, setCart] = useState({});
  const [screen, setScreen] = useState("menu");
  const [note, setNote] = useState("");

  useEffect(() => { if (!live && publicId) track("menu_view"); /* eslint-disable-next-line */ }, [publicId]);
  const track = (event, extra = {}) => {
    if (live || !publicId) return;
    api.post(`/public/qr/${publicId}/engagement`, { event, table: table || "", ...extra }).catch(() => {});
  };
  const add = (it) => {
    setCart((p) => ({ ...p, [it.id]: { item: it, qty: (p[it.id]?.qty || 0) + 1 } }));
    track("cart_add", { item: it.name, qty: 1 });
  };
  const setQty = (id, q) => {
    const cur = cart[id];
    if (!cur) return;
    track(q > cur.qty ? "cart_add" : "cart_remove", { item: cur.item.name, qty: 1 });
    setCart((p) => { const c = { ...p }; if (q <= 0) { delete c[id]; } else { c[id] = { ...c[id], qty: q }; } return c; });
  };
  const minH = live ? "min-h-full" : "min-h-screen";
  const lines = Object.values(cart);
  const count = lines.reduce((s, l) => s + l.qty, 0);
  const total = lines.reduce((s, l) => s + effectivePrice(l.item) * l.qty, 0);

  const btnR = BTN_RADIUS[th.buttonStyle] || "0.75rem";
  const imgR = IMG_RADIUS[th.imageStyle] || "0.9rem";
  const wrap = { background: th.bg, color: th.text, fontFamily: fontStack(th.font), direction: rtl ? "rtl" : "ltr" };

  const renderDish = (it, grid) => {
    const hasSale = priceNum(it.sale_price) > 0 && priceNum(it.sale_price) < priceNum(it.price);
    const soldout = it.available === false;
    return (
      <div className="relative overflow-hidden shadow-sm border" style={{ background: th.card, borderColor: `${th.text}14`, borderRadius: "1rem", opacity: soldout ? 0.6 : 1 }}>
        {it.image ? (
          <div className={grid ? "w-full h-28" : "hidden"} style={{ backgroundImage: `url(${it.image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        ) : null}
        <div className="p-3 flex gap-3">
          {!grid && it.image && <div className="shrink-0 h-16 w-16 bg-cover bg-center" style={{ backgroundImage: `url(${it.image})`, borderRadius: imgR }} />}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold text-sm leading-tight">{it.name || "—"}</div>
              <div className="text-sm font-extrabold whitespace-nowrap" style={{ color: th.primary }}>
                {hasSale && <span className="text-[11px] font-semibold line-through opacity-50 mr-1" style={{ color: th.text }}><bdi>{fmtMoney(it.price, sym)}</bdi></span>}
                {it.price || it.sale_price ? <bdi>{fmtMoney(effectivePrice(it), sym)}</bdi> : ""}
              </div>
            </div>
            {it.description && <div className="text-xs mt-1 opacity-70 line-clamp-2">{it.description}</div>}
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {(it.labels || []).map((l) => <Badge key={l} id={l} lang={lang} style={th.badgeStyle} sym={sym} />)}
              {hasSale && <span className="text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: "#D7263D", color: "#fff" }}>{tr(lang, "off")}</span>}
            </div>
          </div>
          {!cartOn ? null : !soldout ? (
            <button onClick={() => add(it)} data-testid={`menu-add-${it.id}`} className="self-center shrink-0 h-9 w-9 grid place-items-center text-white active:scale-95 transition" style={{ background: th.primary, borderRadius: btnR }} aria-label="Add">
              {cart[it.id] ? <span className="text-xs font-bold">{cart[it.id].qty}</span> : <Icon name="Plus" className="h-4 w-4" />}
            </button>
          ) : <span className="self-center text-[10px] font-bold" style={{ color: "#D7263D" }}>{tr(lang, "soldout")}</span>}
        </div>
      </div>
    );
  };

  // ---------- Waiter screen ----------
  if (screen === "waiter") {
    return (
      <div className={`${minH} flex flex-col`} style={wrap} data-testid="menu-waiter-screen">
        <div className="px-5 pt-6 pb-4 text-white" style={{ background: th.primary }}>
          <button onClick={() => setScreen("cart")} className="text-white/80 text-sm flex items-center gap-1 mb-2"><Icon name="ArrowLeft" className="h-4 w-4" />{tr(lang, "cart")}</button>
          <div className="text-xl font-extrabold">{tr(lang, "showWaiter")}</div>
          {tableLabel && <div className="mt-1 text-white/90 text-sm font-semibold">{tr(lang, "table")} {tableLabel}</div>}
        </div>
        <div className="p-5 flex-1">
          <div className="rounded-2xl border p-4" style={{ background: th.card, borderColor: `${th.text}14` }}>
            <div className="text-xs font-bold uppercase tracking-wide opacity-60 mb-3">{tr(lang, "orderSummary")}</div>
            {lines.map((l) => (
              <div key={l.item.id} className="flex justify-between gap-3 py-2 border-b" style={{ borderColor: `${th.text}10` }}>
                <div className="text-sm"><span className="font-extrabold" style={{ color: th.primary }}>{l.qty}×</span> {l.item.name}</div>
                <div className="text-sm font-bold whitespace-nowrap"><bdi>{fmtMoney(effectivePrice(l.item) * l.qty, sym)}</bdi></div>
              </div>
            ))}
            <div className="flex justify-between items-center pt-3">
              <div className="font-bold">{tr(lang, "total")}</div>
              <div className="text-lg font-extrabold" style={{ color: th.primary }}><bdi>{fmtMoney(total, sym)}</bdi></div>
            </div>
            {note && <div className="mt-3 text-xs rounded-lg p-2" style={{ background: `${th.text}0d` }}><b>{tr(lang, "note")}:</b> {note}</div>}
          </div>
          <p className="text-xs text-center mt-4 opacity-70">{tr(lang, "waiterHint")}</p>
        </div>
        <div className="p-4 sticky bottom-0" style={{ background: th.bg }}>
          <button onClick={() => setScreen("menu")} className="w-full py-3 text-sm font-bold border" style={{ borderRadius: btnR, borderColor: th.primary, color: th.primary }}>{tr(lang, "browse")}</button>
        </div>
      </div>
    );
  }

  // ---------- Cart screen ----------
  if (screen === "cart") {
    return (
      <div className={`${minH} flex flex-col`} style={wrap} data-testid="menu-cart-screen">
        <div className="px-5 pt-6 pb-4 flex items-center gap-2">
          <button onClick={() => setScreen("menu")} className="h-9 w-9 grid place-items-center rounded-full" style={{ background: `${th.text}10` }}><Icon name="ArrowLeft" className="h-4 w-4" /></button>
          <div className="text-lg font-extrabold">{tr(lang, "cart")}</div>
          {tableLabel && <div className="ml-auto text-xs font-bold px-2 py-1 rounded-full" style={{ background: `${th.primary}1a`, color: th.primary }}>{tr(lang, "table")} {tableLabel}</div>}
        </div>
        <div className="px-5 flex-1 space-y-2 overflow-auto">
          {!lines.length && <div className="text-center py-16 opacity-60 text-sm">{tr(lang, "empty")}</div>}
          {lines.map((l) => (
            <div key={l.item.id} className="flex items-center gap-3 rounded-xl border p-3" style={{ background: th.card, borderColor: `${th.text}14` }}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold truncate">{l.item.name}</div>
                <div className="text-xs opacity-70"><bdi>{fmtMoney(effectivePrice(l.item), sym)}</bdi></div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setQty(l.item.id, l.qty - 1)} data-testid={`cart-dec-${l.item.id}`} className="h-7 w-7 grid place-items-center rounded-full border" style={{ borderColor: `${th.text}22` }}><Icon name="Minus" className="h-3.5 w-3.5" /></button>
                <span className="w-5 text-center text-sm font-bold">{l.qty}</span>
                <button onClick={() => setQty(l.item.id, l.qty + 1)} data-testid={`cart-inc-${l.item.id}`} className="h-7 w-7 grid place-items-center rounded-full text-white" style={{ background: th.primary }}><Icon name="Plus" className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
          {lines.length > 0 && (
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={tr(lang, "note")} rows={2} className="w-full rounded-xl border p-3 text-sm mt-2 bg-transparent" style={{ borderColor: `${th.text}22` }} />
          )}
        </div>
        {lines.length > 0 && (
          <div className="p-4 sticky bottom-0 space-y-2" style={{ background: th.bg }}>
            <div className="flex justify-between items-center"><span className="font-bold">{tr(lang, "total")}</span><span className="text-lg font-extrabold" style={{ color: th.primary }}><bdi>{fmtMoney(total, sym)}</bdi></span></div>
            <button data-testid="menu-show-waiter" onClick={() => { track("show_waiter"); setScreen("waiter"); }} className="w-full py-3.5 text-sm font-extrabold text-white flex items-center justify-center gap-2" style={{ background: th.primary, borderRadius: btnR }}><Icon name="Utensils" className="h-4 w-4" />{tr(lang, "callWaiter")}</button>
          </div>
        )}
      </div>
    );
  }

  // ---------- Menu screen ----------
  return (
    <div className={`${minH} pb-24 relative`} style={wrap}>
      {/* Banner */}
      {(d.banners || []).length > 0 ? (
        <div className="w-full h-36 bg-cover bg-center" style={{ backgroundImage: `url(${d.banners[0]})` }} />
      ) : null}
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-3">
          {d.restaurant.logo ? <img src={d.restaurant.logo} alt="" className="h-12 w-12 rounded-xl object-cover shadow" /> : null}
          <div className="min-w-0">
            <h1 className="text-xl font-extrabold leading-tight truncate">{d.restaurant.name || company.name || "Menu"}</h1>
            {d.restaurant.tagline && <p className="text-xs opacity-70">{d.restaurant.tagline}</p>}
          </div>
        </div>
        {(d.restaurant.phone || d.restaurant.address || d.restaurant.hours) && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-75">
            {d.restaurant.phone && <a href={`tel:${d.restaurant.phone}`} className="flex items-center gap-1"><Icon name="Phone" className="h-3 w-3" />{d.restaurant.phone}</a>}
            {d.restaurant.address && <span className="flex items-center gap-1"><Icon name="MapPin" className="h-3 w-3" />{d.restaurant.address}</span>}
            {d.restaurant.hours && <span className="flex items-center gap-1"><Icon name="Clock" className="h-3 w-3" />{d.restaurant.hours}</span>}
          </div>
        )}
        {(d.socials || []).length > 0 && (
          <div className="mt-2 flex gap-2">
            {(d.socials || []).map((s, i) => s.url ? <a key={i} href={s.url} target="_blank" rel="noreferrer" className="h-8 w-8 grid place-items-center rounded-full" style={{ background: `${th.primary}1a`, color: th.primary }}><Icon name={s.icon || "Link"} className="h-4 w-4" /></a> : null)}
          </div>
        )}
        {tableLabel && <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full" style={{ background: `${th.primary}1a`, color: th.primary }}><Icon name="MapPin" className="h-3.5 w-3.5" />{tr(lang, "table")} {tableLabel}</div>}
      </div>

      {/* Featured strip */}
      {featured.length > 0 && (
        <div className="pl-5 mb-2">
          <div className="text-xs font-bold uppercase tracking-wide opacity-60 mb-2 flex items-center gap-1"><Icon name="Sparkles" className="h-3.5 w-3.5" style={{ color: th.primary }} />{tr(lang, "featured")}</div>
          <div className="flex gap-3 overflow-x-auto pb-2 pr-5 no-scrollbar">
            {featured.map((it) => (
              <div key={"f" + it.id} className="shrink-0 w-40 rounded-2xl overflow-hidden border shadow-sm" style={{ background: th.card, borderColor: `${th.text}14` }}>
                {it.image ? <div className="h-24 w-full bg-cover bg-center" style={{ backgroundImage: `url(${it.image})` }} />
                  : <div className="h-24 w-full grid place-items-center" style={{ background: `${th.primary}14`, color: th.primary }}><Icon name="UtensilsCrossed" className="h-7 w-7 opacity-60" /></div>}
                <div className="p-2.5">
                  <div className="text-xs font-bold truncate">{it.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs font-extrabold" style={{ color: th.primary }}>
                      {priceNum(it.sale_price) > 0 && priceNum(it.sale_price) < priceNum(it.price) && <span className="text-[10px] font-semibold line-through opacity-50 mr-1" style={{ color: th.text }}><bdi>{fmtMoney(it.price, sym)}</bdi></span>}
                      <bdi>{fmtMoney(effectivePrice(it), sym)}</bdi>
                    </span>
                    {cartOn && it.available !== false && <button data-testid={`menu-feat-add-${it.id}`} onClick={() => add(it)} className="h-6 w-6 grid place-items-center rounded-full text-white" style={{ background: th.primary }}><Icon name="Plus" className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="px-5 space-y-6 mt-2">
        {cats.map((c) => (
          <div key={c.id} data-testid={`menu-cat-${c.id}`}>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-base font-extrabold">{c.name}</h2>
              <div className="flex-1 h-px" style={{ background: `${th.text}14` }} />
            </div>
            <div className={th.layout === "grid" ? "grid grid-cols-2 gap-3" : "space-y-3"}>
              {(c.items || []).map((it) => <React.Fragment key={it.id}>{renderDish(it, th.layout === "grid")}</React.Fragment>)}
            </div>
          </div>
        ))}
        {!cats.length && <p className="text-sm opacity-60 py-10 text-center">No menu items yet.</p>}
      </div>

      {/* Floating cart */}
      {cartOn && count > 0 && (
        <div className="fixed left-0 right-0 bottom-0 p-4 z-20" style={{ maxWidth: 480, margin: "0 auto" }}>
          <button data-testid="menu-view-cart" onClick={() => setScreen("cart")} className="w-full py-3.5 text-sm font-extrabold text-white flex items-center justify-between px-5 shadow-lg active:scale-[0.99] transition" style={{ background: th.primary, borderRadius: btnR }}>
            <span className="flex items-center gap-2"><span className="h-6 min-w-6 px-1.5 grid place-items-center rounded-full bg-white/25 text-xs">{count}</span>{tr(lang, "review")}</span>
            <span><bdi>{fmtMoney(total, sym)}</bdi></span>
          </button>
        </div>
      )}
    </div>
  );
}
